import axios from 'axios';
import {
  isMotorPolicyDetails,
  isMotorPolicyNumber,
} from '@/utils/gisPolicyNumber';
import { motorRecencyKey, sortByRecency } from '@/utils/policyRecency';
import { MOTOR_PRODUCT_GROUP } from '@/constants/motorPrivate';

export const MOTOR_POLICY_FETCH_LIMIT = 25;

export function motorCoverLabel(coverType) {
  const raw = String(coverType || 'Motor Private')
    .replace(/\s*Insurance\s*/gi, ' ')
    .trim();
  return raw || 'Motor Private';
}

export function primaryMotorRisk(policy) {
  const risks = policy?.risks;
  if (!Array.isArray(risks) || risks.length === 0) return null;
  return risks.find((risk) => Number(risk.suminsured) > 0) || risks[0];
}

export function motorVehicleLine(policy) {
  const risk = primaryMotorRisk(policy);
  if (!risk) return '—';
  const model = risk.model || risk.make || '';
  const reg = risk.regno ? String(risk.regno).toUpperCase() : '';
  if (model && reg) return `${model}, ${reg}`;
  return model || reg || '—';
}

export function motorRiskYear(risk, policy) {
  if (risk?.yearOfManufacture) return String(risk.yearOfManufacture);
  if (risk?.year) return String(risk.year);
  if (policy?.polUwYear) return String(policy.polUwYear);
  return '';
}

function publishMotorPolicies(policies, onUpdate) {
  if (!policies.length || typeof onUpdate !== 'function') return;
  onUpdate(sortByRecency(policies, motorRecencyKey));
}

async function loadMotorProductGroup(client, limit, requestConfig) {
  if (!client?.clientCode) return [];
  try {
    const response = await axios.get(
      `/customer/gis/policies/client/productGroup/${MOTOR_PRODUCT_GROUP}`
        + `?clientCode=${client.clientCode}&pageNumber=1&limit=${limit}`,
      requestConfig,
    );
    const items = response.data?.object?.pageItems;
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
}

async function loadMotorIdStubs(nationalId, requestConfig) {
  if (!nationalId) return [];
  try {
    const response = await axios.get(
      `/customer/gis/policies/byidnumber/${nationalId}`,
      requestConfig,
    );
    return (response.data?.object || []).filter((policy) => isMotorPolicyNumber(policy.policyNo));
  } catch {
    return [];
  }
}

export async function fetchMotorPolicyRecords({
  client,
  clientPromise,
  nationalId,
  requestConfig = {},
  limit = MOTOR_POLICY_FETCH_LIMIT,
  onUpdate,
}) {
  const policies = [];
  const seen = new Set();

  function add(policy) {
    const policyNo = policy?.policyNo;
    if (!policyNo || seen.has(policyNo)) return;
    if (!isMotorPolicyDetails(policy)) return;
    seen.add(policyNo);
    policies.push(policy);
  }

  const groupTask = Promise.resolve(client?.clientCode ? client : clientPromise)
    .catch(() => null)
    .then((resolvedClient) => loadMotorProductGroup(resolvedClient, limit, requestConfig))
    .then((items) => {
      items.forEach(add);
      publishMotorPolicies(policies, onUpdate);
    });
  const stubTask = loadMotorIdStubs(nationalId, requestConfig);
  const [, stubs] = await Promise.all([groupTask, stubTask]);

  await Promise.all(stubs.map(async (stub) => {
    if (seen.has(stub.policyNo)) return;
    try {
      const detail = await axios.get(
        `/customer/gis/policies/details?policyNo=${encodeURIComponent(stub.policyNo)}`,
        requestConfig,
      );
      if (detail.data?.object) add(detail.data.object);
    } catch {
      /* skip if details fail */
    }
  }));

  const sorted = sortByRecency(policies, motorRecencyKey);
  publishMotorPolicies(sorted, onUpdate);
  return sorted;
}
